import { baseApi } from '../apiProvider';
import { RequestAddUser } from '../models/request/requestAddUser';
import { RequestUpdatePrivateProfile } from '../models/request/requestUpdatePrivateProfile';
import { RequestUpdateUserInfo } from '../models/request/requestUpdateUserInfo';
import { ResponseExperienceType } from '../models/response/responseExperienceType';
import { ConsumptionHistory } from '../models/response/responseConsumptionHistory';
import { DepositHistory } from '../models/response/responseDepositHistory';
import { ResponsePrivateProfile } from '../models/response/responsePrivateProfile';
import { ResponseTermsAgreementDate } from '../models/response/responseTermsAgreementDate';
import { ResponseTokenInfo } from '../models/response/responseTokenInfo';
import {
  ResponseUserExperience,
  ResponseUser,
} from '../models/response/responseUser';
import { ResponseUserCode } from '../models/response/responseUserCode';
import { ResponseUserDiamondCoin } from '../models/response/responseUserDiamondCoin';
import { ResponseUserRegister } from '../models/response/responseUserRegister';

// user API
const userAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // create user
    addUser: builder.mutation<ResponseUserRegister, RequestAddUser>({
      query: (user: RequestAddUser) => ({
        url: '/v0/users',
        method: 'POST',
        body: user,
      }),
    }),
    // check token info
    getTokenInfo: builder.mutation<ResponseTokenInfo, string>({
      query: (token: string) => ({
        url: `/v0/users/tokenInfo?token=${token}`,
        method: 'GET',
      }),
    }),
    // get user info
    getUserInfo: builder.query<ResponseUser, void>({
      query: () => ({
        url: `/v0/users/me`,
        method: 'GET',
      }),
      providesTags: ['User'],
      keepUnusedDataFor: 0,
    }),
    // update user info
    updateUserInfo: builder.mutation<ResponseUser, RequestUpdateUserInfo>({
      query: (body: RequestUpdateUserInfo) => ({
        url: `/v0/users/me`,
        method: 'PUT',
        body,
      }),
    }),
    // update user's login ID
    updateSelfLoginId: builder.mutation<ResponseUser, string>({
      query: (newId: string) => ({
        url: '/v0/users/me/loginId',
        method: 'PUT',
        body: {
          loginId: newId,
        },
      }),
      invalidatesTags: ['User'],
    }),
    // validate login ID is unique
    validateLoginId: builder.mutation<boolean, string>({
      query: (loginId: string) => ({
        url: `/v0/provisional-users?loginId=${loginId}`,
        method: 'GET',
      }),
      transformResponse: (rawResult: unknown[]) => {
        return rawResult.length <= 0;
      },
    }),
    // get user coins
    getUserCoins: builder.mutation<ResponseUserDiamondCoin, void>({
      query: () => ({
        url: `/v0/users/me/diamond-coins`,
        method: 'GET',
      }),
    }),
    // get private profile of login user
    getSelfPrivateProfile: builder.mutation<ResponsePrivateProfile, void>({
      query: () => ({
        url: '/v0/users/me/private-profile',
        method: 'GET',
      }),
    }),
    // update private profile of login user
    updateSelfPrivateProfile: builder.mutation<
      ResponsePrivateProfile,
      RequestUpdatePrivateProfile
    >({
      query: (body: RequestUpdatePrivateProfile) => ({
        url: '/v0/users/me/private-profile',
        method: 'PUT',
        body,
      }),
    }),
    // update avater of login user
    updateSelfAvatar: builder.mutation<ResponseUser, string>({
      query: (resourceId: string) => ({
        url: '/v0/users/me/picture',
        method: 'PUT',
        body: {
          id: resourceId,
        },
      }),
      invalidatesTags: ['User'],
    }),
    // get live terms agreement date
    getTermsAgreementDate: builder.query<ResponseTermsAgreementDate, void>({
      query: () => ({
        url: `/v0/users/me/terms-confirmation-date`,
        method: 'GET',
      }),
    }),
    // set live terms agreement date
    setTermsAgreementDate: builder.mutation<ResponseTermsAgreementDate, void>({
      query: () => ({
        url: `/v0/users/me/terms-confirmation-date`,
        method: 'PUT',
      }),
    }),
    // get user deposit history
    getDepositHistory: builder.mutation<DepositHistory[], void>({
      query: () => ({
        url: `/v0/users/me/daimond/deposit-histories`,
        method: 'GET',
      }),
    }),
    // get user consumption history
    getConsumptionHistory: builder.mutation<ConsumptionHistory[], void>({
      query: () => ({
        url: `/v0/users/me/daimond/withdrawal-histories`,
        method: 'GET',
      }),
    }),
    // get user code
    getUserCode: builder.mutation<ResponseUserCode, void>({
      query: () => ({
        url: `/v0/user-secret-codes`,
        method: 'GET',
      }),
    }),
    // post user code
    postUserCode: builder.mutation<ResponseUserCode, void>({
      query: () => ({
        url: `/v0/user-secret-codes`,
        method: 'POST',
      }),
    }),
    // get user code
    getUserExp: builder.mutation<ResponseUserExperience, void>({
      query: () => ({
        url: `/v0/experiences`,
        method: 'GET',
      }),
    }),
    // get experience values for each level
    getExperienceLevels: builder.mutation<ResponseExperienceType[], void>({
      query: () => ({
        url: `/v0/experience-types`,
        method: 'GET',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddUserMutation,
  useGetTokenInfoMutation,
  useGetUserInfoQuery,
  useUpdateUserInfoMutation,
  useUpdateSelfLoginIdMutation,
  useValidateLoginIdMutation,
  useGetUserCoinsMutation,
  useGetSelfPrivateProfileMutation,
  useUpdateSelfPrivateProfileMutation,
  useUpdateSelfAvatarMutation,
  useGetTermsAgreementDateQuery,
  useSetTermsAgreementDateMutation,
  useGetDepositHistoryMutation,
  useGetConsumptionHistoryMutation,
  useGetUserCodeMutation,
  usePostUserCodeMutation,
  useGetUserExpMutation,
  useGetExperienceLevelsMutation,
} = userAPI;
